/**
 * The small controls the Job and Appointment sections share —
 * `docs/new-request.md` §8, §9, §16.3, Boards 6, 15.
 *
 * On a desktop every choice is the form kit's own drop-down; on a phone it is
 * a 44 px field that opens a bottom sheet of 52 px rows (§16.3: no native select,
 * every picker a bottom sheet). The chips, the By · On · After · Between track
 * and the grade tag are drawn here once so the two sections cannot draw them
 * two ways.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { cn } from '../lib/cn.js'
import { PanelSheet } from '../components/panel-sheet.js'
import { controlClass } from '../components/form/field.js'
import { IconCheck, IconChevronRight, IconPlus, IconSearch } from '../frame/icons.js'
import type { Grade } from '@rowted/shared'

/** One answer in a phone picker. */
export interface PickOption {
  value: string
  label: string
  /** A tile or a glyph before the label — a division's mark. */
  lead?: ReactNode
  /** Quiet words at the right — a lead source's jobs this year. */
  detail?: string
}

/** The 44 px box a phone field is (Board 15): the kit's `touch` control. */
export function phoneBox(invalid?: boolean): string {
  return controlClass({ invalid, size: 'touch', className: 'text-left font-medium' })
}

/**
 * A phone picker: the 44 px field, and the bottom sheet of 52 px rows it
 * opens. `search` adds a box at the top of the sheet; `onAdd` a last row
 * offering what was typed (*Add “Nextdoor” as a lead source*).
 */
export function PhonePicker({
  label, title, value, display, options, placeholder, onChange, invalid, search, onAdd, addWords,
  field,
}: {
  label: string
  /** The sheet's title; the field's label when left out. */
  title?: string
  value: string | null
  /** What the field reads when the value is not one of the options (a new lead source). */
  display?: string | null
  options: PickOption[]
  placeholder?: string
  onChange: (value: string | null) => void
  invalid?: boolean
  search?: boolean
  onAdd?: (text: string) => void
  addWords?: (text: string) => string
  /** `data-request-field`, so the sheet can put the caret here. */
  field?: string
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  useEffect(() => { if (!open) setQuery('') }, [open])

  const chosen = options.find((option) => option.value === value) ?? null
  const shown = useMemo(() => {
    const wanted = query.trim().toLowerCase()
    return wanted ? options.filter((option) => option.label.toLowerCase().includes(wanted)) : options
  }, [options, query])
  const typed = query.trim()
  const exact = options.some((option) => option.label.toLowerCase() === typed.toLowerCase())

  return (
    <>
      <button
        type="button"
        aria-label={label}
        aria-haspopup="dialog"
        data-request-field={field}
        onClick={() => setOpen(true)}
        className={phoneBox(invalid)}
      >
        {chosen?.lead}
        <span className={cn('min-w-0 flex-1 truncate', !chosen && !display && 'text-text-faint')}>
          {chosen?.label ?? display ?? placeholder ?? 'Choose…'}
        </span>
        {/* A phone's picker is a sheet one level down, so it points right (Boards 14–15). */}
        <span className="shrink-0 text-text-faint"><IconChevronRight size={15} /></span>
      </button>
      <PanelSheet open={open} onClose={() => setOpen(false)} title={title ?? label} layer={70}>
        {search && (
          <div className="border-b border-line-faint px-4 py-2.5">
            <span className={phoneBox()}>
              <span className="text-text-faint"><IconSearch size={16} /></span>
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Find…"
                aria-label={`Find a ${label.toLowerCase()}`}
                className="min-w-0 flex-1 bg-transparent text-[16px] outline-none focus-visible:shadow-none placeholder:text-text-faint"
              />
            </span>
          </div>
        )}
        <div role="listbox" aria-label={label} className="flex flex-col">
          {shown.map((option) => {
            const on = option.value === value
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={on}
                onClick={() => { onChange(option.value); setOpen(false) }}
                className={cn('flex h-[52px] w-full items-center gap-3 border-b border-line-faint px-4',
                  'text-left', on ? 'bg-green-band-bg' : 'bg-surface')}
              >
                {option.lead}
                <span className="min-w-0 flex-1 truncate text-[15px] font-medium text-text">{option.label}</span>
                {option.detail && <span className="shrink-0 text-[12.5px] text-text-faint">{option.detail}</span>}
                <span className={cn('inline-flex h-5 w-5 shrink-0 items-center justify-center',
                  on ? 'text-accent' : 'text-transparent')}>
                  <IconCheck size={16} />
                </span>
              </button>
            )
          })}
          {onAdd && typed && !exact && (
            <button
              type="button"
              onClick={() => { onAdd(typed); setOpen(false) }}
              className={cn('flex h-[52px] w-full items-center gap-2 px-4 text-left text-[15px]',
                'font-semibold text-accent')}
            >
              <IconPlus size={16} />
              {addWords ? addWords(typed) : `Add “${typed}”`}
            </button>
          )}
        </div>
      </PanelSheet>
    </>
  )
}

/** A choice chip — 30 px on a desktop, 32 on a phone, a tick when it is on (Board 6). */
export function PickChip({ on, onClick, phone, children }: {
  on: boolean
  onClick: () => void
  phone?: boolean
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-chip border px-2.5',
        'text-[12px] font-semibold',
        phone ? 'h-8' : 'h-[30px]',
        on ? 'border-accent-line bg-accent-soft text-accent-dark'
          : 'border-line bg-surface text-text hover:border-line-strong',
      )}
    >
      {on && <IconCheck size={12} />}
      {children}
    </button>
  )
}

/**
 * The segmented track of *By · On · After · Between* — 24 px segments on a
 * desktop, 34 on a phone, on the field-grey track (Board 6, Board 15).
 */
export function ModeTrack<T extends string>({ options, value, onChange, label, phone }: {
  options: { value: T; label: string }[]
  value: T
  onChange: (value: T) => void
  label: string
  phone?: boolean
}) {
  return (
    <span role="radiogroup" aria-label={label}
      className="inline-flex shrink-0 rounded-[10px] bg-line-soft p-[3px]">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={option.value === value}
          onClick={() => onChange(option.value)}
          className={cn('inline-flex items-center justify-center rounded-button px-3 font-semibold',
            phone ? 'h-[34px] text-[13px]' : 'h-6 text-[12px]',
            option.value === value ? 'bg-surface text-text shadow-segmented' : 'text-text-muted hover:text-text')}
        >
          {option.label}
        </button>
      ))}
    </span>
  )
}

/** *Best · Good · OK* as the board's tag (Board 6, 7). */
export function GradeTag({ grade }: { grade: Grade }) {
  return (
    <span className={cn('inline-flex h-5 items-center whitespace-nowrap rounded-tag px-1.5',
      'text-[11px] font-semibold',
      grade === 'best' ? 'bg-accent-soft text-accent-dark'
        : grade === 'good' ? 'bg-teal-band-bg text-teal-band-text'
          : 'bg-amber-band-bg text-amber-band-text')}>
      {grade === 'best' ? 'Best' : grade === 'good' ? 'Good' : 'OK'}
    </span>
  )
}

/** The pointer is a finger or a pencil: dates open the system picker (§8.1). */
export function useCoarsePointer(): boolean {
  const [coarse, setCoarse] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches)
  useEffect(() => {
    const query = window.matchMedia?.('(pointer: coarse)')
    if (!query) return
    const change = () => setCoarse(query.matches)
    query.addEventListener('change', change)
    return () => query.removeEventListener('change', change)
  }, [])
  return Boolean(coarse)
}
